import { useQuery } from "@tanstack/react-query";
import {
  getGainApy,
  getGainTVL,
  getTVL,
  TGainApyResponse,
} from "@services/index";
import { useIdle } from "react-use";
import { toFixedWithoutRounding } from "@constants/common";
import { numberToCurrency } from "utils";
import { useMemo } from "react";

export type TVaultType =
  | "agETH"
  | "hgETH"
  | "sbUSD" // Stable vault (maps to rsETH in API)
  | "xgUSD"
  | "all";

const VAULT_DATA_MAP = {
  agETH: { apyKey: "agETH" as const, tvlKey: "agEthTvl" as const },
  hgETH: { apyKey: "hgETH" as const, tvlKey: "hgEthTvl" as const },
  sbUSD: { apyKey: "rsETH" as const, tvlKey: "sbUsdTvl" as const }, // API uses rsETH for sbUSD
  xgUSD: { apyKey: "xgUSD" as const, tvlKey: null },
} as const;

type TVaultDataMap = typeof VAULT_DATA_MAP;

/**
 * Helper function to format vault data
 */
const formatVaultData = (
  apyData: any,
  ethPrice: number,
  apyKey: keyof TGainApyResponse,
  tvlValue: number | null | undefined,
  calculateEthValue: boolean = true
) => {
  const rawApy = apyData?.[apyKey];
  const apy = rawApy && !isNaN(+rawApy) ? +rawApy : null;
  const tvl = tvlValue ?? null;
  const ethStaked =
    calculateEthValue && tvl && ethPrice ? tvl / ethPrice : null;

  return {
    apy: rawApy,
    tvl,
    totalStaked: tvl, // Kept for backward compatibility
    ethStaked,
    totalStakedEth: ethStaked, // Kept for backward compatibility
    formattedApy: apy ? `${toFixedWithoutRounding(apy.toString(), 2)}%` : "-",
    formattedTvl:
      tvl && !isNaN(Number(tvl))
        ? `${numberToCurrency(tvl.toFixed(2), 1)}`
        : "-",
    formattedETHStaked:
      ethStaked && !isNaN(Number(ethStaked))
        ? `${toFixedWithoutRounding(ethStaked.toString(), 2)} ETH`
        : "",
  };
};

// Core vault data shape (without loading states)
type TVaultDataCore = {
  apy: any;
  tvl: number | null;
  ethStaked: number | null;
  formattedApy: string;
  formattedTvl: string;
  formattedETHStaked: string;
  // Deprecated fields (kept for backward compatibility)
  totalStaked: number | null; // Use tvl instead
  totalStakedEth: number | null; // Use ethStaked instead
};

// Individual vault data with loading states
type TVaultData = TVaultDataCore & {
  isLoadingApy: boolean;
  isLoadingTvl: boolean;
  isLoading: boolean;
};

// All vaults response - only exposed data consumers need
type TAllVaultsData = {
  isLoadingApy: boolean;
  isLoadingTvl: boolean;
  totalTvl: number;
  formattedTotalTvl: string;
  agETH: TVaultDataCore;
  hgETH: TVaultDataCore;
  sbUSD: TVaultDataCore;
  xgUSD: TVaultDataCore;
};

/**
 * Fetch vault APY and TVL data
 * @param vault - Specific vault or "all" for all vaults
 */
// eslint-disable-next-line no-redeclare
export function useVaultTvlApy(vault: "all"): TAllVaultsData;
// eslint-disable-next-line no-redeclare
export function useVaultTvlApy(vault: Exclude<TVaultType, "all">): TVaultData;
// eslint-disable-next-line no-redeclare
export function useVaultTvlApy(vault: TVaultType = "all"): any {
  const isIdle = useIdle(150e3);

  // Fetch APY data for all vaults
  const { data: apyData, isLoading: isLoadingApy } = useQuery({
    queryKey: ["gainApy"],
    queryFn: async () => {
      const { data } = await getGainApy();
      return data || {};
    },
    enabled: !isIdle,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });

  // Fetch TVL data for all vaults
  const { data: tvlData, isLoading: isLoadingTvl } = useQuery({
    queryKey: ["gainTvl"],
    queryFn: async () => {
      const { data } = await getGainTVL();
      return data;
    },
    enabled: !isIdle,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });

  // Fetch rsETH TVL to calculate ETH price
  const { data: rsEthTvlData, isLoading: isLoadingRsEthTvl } = useQuery({
    queryKey: ["tvl"],
    queryFn: async () => {
      const { data } = await getTVL();
      return data;
    },
    enabled: !isIdle,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });

  // Calculate ETH price from rsETH TVL
  const ethPrice = useMemo(() => {
    if (rsEthTvlData?.value && rsEthTvlData?.usdTvl) {
      return rsEthTvlData.usdTvl / rsEthTvlData.value;
    }
    return 0;
  }, [rsEthTvlData]);

  // Return formatted data for all vaults if "all" is requested
  const formattedData = useMemo(() => {
    if (vault.toLowerCase() === "all") {
      const totalTvl = tvlData?.tvl ?? 0;
      const formattedTotalTvl =
        totalTvl > 0
          ? `$${numberToCurrency(
              toFixedWithoutRounding(totalTvl.toString(), 2),
              2
            )}`
          : "-";

      return {
        isLoadingApy,
        isLoadingTvl,
        totalTvl,
        formattedTotalTvl,
        agETH: formatVaultData(
          apyData,
          ethPrice,
          "agETH",
          tvlData?.agEthTvl,
          true
        ),
        hgETH: formatVaultData(
          apyData,
          ethPrice,
          "hgETH",
          tvlData?.hgEthTvl,
          true
        ),
        sbUSD: formatVaultData(
          apyData,
          ethPrice,
          "rsETH", // API uses rsETH for sbUSD
          tvlData?.sbUsdTvl,
          false
        ),
        xgUSD: formatVaultData(apyData, ethPrice, "xgUSD", null, true),
      };
    }

    // Get vault-specific configuration using lowercase comparison
    const vaultKey = vault as keyof TVaultDataMap;
    const vaultConfig = VAULT_DATA_MAP[vaultKey];

    if (!vaultConfig) {
      // This should never happen since all vault types are mapped
      return {
        apy: null,
        tvl: null,
        totalStaked: null,
        ethStaked: null,
        totalStakedEth: null,
        formattedApy: "-",
        formattedTvl: "-",
        formattedETHStaked: "",
        isLoadingApy,
        isLoadingTvl,
        isLoading: isLoadingApy || isLoadingTvl || isLoadingRsEthTvl,
      };
    }

    // Get TVL value
    const tvlValue: number | null =
      vaultConfig.tvlKey && tvlData
        ? tvlData[vaultConfig.tvlKey] ?? null
        : null;

    // Determine if ETH value calculation is needed (not for sbUSD/stable vault)
    const includeEthValue = vault.toLowerCase() !== "sbusd";

    // Use helper function to format data
    const vaultData = formatVaultData(
      apyData,
      ethPrice,
      vaultConfig.apyKey,
      tvlValue,
      includeEthValue
    );

    return {
      ...vaultData,
      // Loading states
      isLoadingApy,
      isLoadingTvl,
      isLoading: isLoadingApy || isLoadingTvl || isLoadingRsEthTvl,
    };
  }, [
    vault,
    apyData,
    tvlData,
    isLoadingApy,
    isLoadingTvl,
    isLoadingRsEthTvl,
    ethPrice,
  ]);

  return formattedData;
}
